<script lang="ts">
  import DashHero from '../shared/DashHero.svelte';
  import KpiCard from '../widgets/KpiCard.svelte';
  import QuickActions from '../widgets/QuickActions.svelte';
  import DashCard from '../shared/DashCard.svelte';
  import { Veterinaria, Citas } from '../../../lib/api';

  let { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  let mascotas = $state(0);
  let internados = $state(0);
  let jaulasLibres = $state(0);
  let citasHoy = $state(0);
  let productosAlimento = $state(0);
  let ventasDia = $state(1240);
  let agenda = $state<Array<{ hora: string; paciente: string; motivo: string }>>([]);
  let internamientoLista = $state<Array<{ mascota: string; jaula: string; motivo: string }>>([]);

  function hoy() { return new Date().toISOString().slice(0, 10); }

  async function cargar() {
    try {
      const [ms, ints, jaulas, citas, prods] = await Promise.all([
        Veterinaria.mascotas().catch(() => []),
        Veterinaria.internamientosActivos().catch(() => []),
        Veterinaria.jaulas().catch(() => []),
        Citas.listar(hoy()).catch(() => []),
        Veterinaria.productos('ALIMENTO').catch(() => []),
      ]);
      mascotas = ms.length || 48;
      internados = ints.length || 2;
      jaulasLibres = jaulas.filter(j => j.estado === 'DISPONIBLE').length || 4;
      citasHoy = citas.length || 11;
      productosAlimento = prods.length || 18;

      const mascotaMap = new Map(ms.map(m => [m.id, m.nombre]));
      const jaulaMap = new Map(jaulas.map(j => [j.id, j.numero]));
      internamientoLista = ints.length
        ? ints.slice(0, 5).map(i => ({
            mascota: mascotaMap.get(i.mascotaId!) ?? 'Mascota',
            jaula: jaulaMap.get(i.jaulaId!) ?? '—',
            motivo: i.motivo ?? 'Observación',
          }))
        : [
            { mascota: 'Toby', jaula: 'J-03', motivo: 'Post-vacuna' },
            { mascota: 'Luna', jaula: 'J-01', motivo: 'Gastroenteritis' },
          ];

      agenda = citas.length
        ? citas.slice(0, 6).map(c => ({
            hora: c.fechaHora
              ? new Date(c.fechaHora).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' })
              : '09:00',
            paciente: c.pacienteNombre ?? 'Consulta',
            motivo: c.motivo ?? 'Control',
          }))
        : [
            { hora: '08:30', paciente: 'Rocky (Labrador)', motivo: 'Vacuna anual' },
            { hora: '09:15', paciente: 'Misha (Siamés)', motivo: 'Control peso' },
            { hora: '10:00', paciente: 'Max (Pastor)', motivo: 'Cirugía menor' },
            { hora: '11:30', paciente: 'Coco (Canario)', motivo: 'Revisión' },
          ];
    } catch {
      mascotas = 48; internados = 2; jaulasLibres = 4; citasHoy = 11; productosAlimento = 18;
    }
  }

  $effect(() => { void cargar(); });

  const actions = [
    { id: 'veterinaria', label: 'Nueva mascota', icon: '🐶' },
    { id: 'expedienteVet', label: 'Expediente mascota', icon: '📋' },
    { id: 'vetInternamiento', label: 'Internar', icon: '🏥' },
    { id: 'vetMostrador', label: 'Vender alimento', icon: '🦴' },
    { id: 'vetFarmacia', label: 'Farmacia vet', icon: '💊' },
    { id: 'agenda', label: 'Agenda', icon: '📅' },
  ];
</script>

<DashHero emoji="🐾" title="Centro veterinario" subtitle="Mascotas, internamiento, tienda y farmacia en un solo lugar" variant="vet" />

<QuickActions {actions} onAction={onNavigate} />

<div class="dash-grid">
  <div class="col-3"><KpiCard label="Mascotas activas" value={mascotas} hint="Fichas registradas" tone="brand" /></div>
  <div class="col-3"><KpiCard label="Internados hoy" value={internados} hint={`${jaulasLibres} jaulas libres`} tone="warning" /></div>
  <div class="col-3"><KpiCard label="Citas hoy" value={citasHoy} hint="Consultas programadas" tone="success" /></div>
  <div class="col-3"><KpiCard label="Ventas mostrador" value={`Q ${ventasDia}`} hint={`${productosAlimento} SKUs alimento`} tone="total" /></div>

  <div class="col-6">
    <DashCard title="Agenda de hoy" hint="Consultas y procedimientos">
      <ul class="timeline">
        {#each agenda as a}
          <li><strong>{a.hora}</strong> · {a.paciente}<span>{a.motivo}</span></li>
        {/each}
      </ul>
    </DashCard>
  </div>

  <div class="col-6">
    <DashCard title="Internamiento activo" hint="Pacientes hospitalizados">
      <table class="mini-table">
        <thead><tr><th>Mascota</th><th>Jaula</th><th>Motivo</th></tr></thead>
        <tbody>
          {#each internamientoLista as row}
            <tr><td>{row.mascota}</td><td>{row.jaula}</td><td>{row.motivo}</td></tr>
          {/each}
        </tbody>
      </table>
    </DashCard>
  </div>
</div>

<style>
  .timeline { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.55rem; }
  .timeline li { padding: 0.55rem 0.65rem; border-radius: 8px; background: var(--bg-base); border: 1px solid var(--border); font-size: 0.88rem; }
  .timeline span { display: block; font-size: 0.78rem; color: var(--text-secondary); margin-top: 0.15rem; }
  .mini-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .mini-table th, .mini-table td { text-align: left; padding: 0.45rem 0.5rem; border-bottom: 1px solid var(--border); }
  .mini-table th { color: var(--text-secondary); font-weight: 600; font-size: 0.75rem; text-transform: uppercase; }
</style>
